import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Space, Input, Select, DatePicker, Row, Col, message, Popconfirm, Tag, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as invoiceApi from '../../services/invoiceApi';
import { useChannels } from '../../hooks/useChannels';
import EmptyState from '../../components/EmptyState';

const { Option } = Select;
const { RangePicker } = DatePicker;

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const { channels } = useChannels();
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line
  }, [search, channel, status, dateRange]);


  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (channel) params.channel = channel;
      if (status) params.status = status;
      if (dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await invoiceApi.getInvoices(params);
      console.log('Invoice response:', response); // Debug log
      setInvoices(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (id) => {
    try {
      const response = await invoiceApi.downloadInvoicePDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      message.error('Failed to download invoice');
    }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      await invoiceApi.deleteInvoice(id);
      message.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      message.error('Failed to delete invoice');
    }
  };

  const handleMarkAsPaid = async (id) => {
    try {
      await invoiceApi.markInvoicePaid(id);
      message.success('Invoice and related jobs marked as paid.');
      fetchInvoices();
    } catch (error) {
      message.error('Failed to mark invoice as paid');
    }
  };

  // Function to render colored status tags
  const renderStatusTag = (status) => {
    let color = 'default';
    let text = status;

    switch (status?.toLowerCase()) {
      case 'paid':
        color = 'success';
        text = 'Paid';
        break;
      case 'unpaid':
        color = 'error';
        text = 'Unpaid';
        break;
      default:
        color = 'default';
        text = status || 'Unknown';
    }

    return <Tag color={color}>{text}</Tag>;
  };

  // Function to clear all filters
  const clearFilters = () => {
    setSearch('');
    setChannel('');
    setStatus('');
    setDateRange([]);
  };

  const columns = [
    { title: 'Invoice #', dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
    { title: 'Date', dataIndex: 'invoiceDate', key: 'invoiceDate', render: v => dayjs(v).format('YYYY-MM-DD') },
    { title: 'Total', dataIndex: 'total', key: 'total', render: v => `£${v?.toFixed(2)}` },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => renderStatusTag(status) },
    { title: 'Channel', dataIndex: 'channel', key: 'channel' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button onClick={() => navigate(`/invoices/${record._id}`)}>View</Button>
          <Button onClick={() => handleDownloadInvoice(record._id)}>Download PDF</Button>
          {record.status !== 'Paid' && (
            <Popconfirm
              title="Mark this invoice and all related jobs as paid?"
              onConfirm={() => handleMarkAsPaid(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary">Mark as Paid</Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Are you sure you want to delete this invoice?"
            onConfirm={() => handleDeleteInvoice(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Invoices"
      extra={<Button type="primary" onClick={() => navigate('/invoices/create')}>Create Invoice</Button>}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Input
            placeholder="Search by invoice # or customer"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col span={5}>
          <Select
            placeholder="Filter by channel"
            value={channel || undefined}
            onChange={setChannel}
            allowClear
            style={{ width: '100%' }}
          >
            {channels.map(ch => (
              <Option key={ch._id || ch.name} value={ch.name}>
                {ch.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={5}>
          <Select
            placeholder="Filter by status"
            value={status || undefined}
            onChange={setStatus}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="Paid">Paid</Option>
            <Option value="Unpaid">Unpaid</Option>
          </Select>
        </Col>
        <Col span={5}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={4}>
          <Button onClick={clearFilters} style={{ width: '100%' }}>
            Clear Filters
          </Button>
        </Col>
      </Row>
      
      {/* Filter Summary */}
      {(search || channel || status || dateRange.length > 0) && (
        <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 6 }}>
          <strong>Active Filters:</strong>
          {search && <Tag style={{ marginLeft: 8 }}>Search: "{search}"</Tag>}
          {channel && <Tag style={{ marginLeft: 8 }}>Channel: {channel}</Tag>}
          {status && <Tag style={{ marginLeft: 8 }}>Status: {status}</Tag>}
          {dateRange.length > 0 && (
            <Tag style={{ marginLeft: 8 }}>
              Date: {dateRange[0].format('YYYY-MM-DD')} to {dateRange[1].format('YYYY-MM-DD')}
            </Tag>
          )}
        </div>
      )}
      
      {error && (
        <Alert
          message="Failed to load invoices"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={fetchInvoices}>Retry</Button>}
        />
      )}

      <Table
        columns={columns}
        dataSource={invoices}
        rowKey="_id"
        loading={loading}
        locale={{ emptyText: <EmptyState message="No invoices found" hint="Try adjusting your filters" /> }}
      />
    </Card>
  );
};

export default InvoiceList; 